import { UserService } from "../services/user.service";
import { TokenService } from "../services/storage.service";
import router from "../router";

const AuthType = Object.freeze({
  Login: 1,
  Verify: 2,
  RefshToken: 3,
  Logout: 4
})

const getDefaultState = (authError = "", authType = 0) => {
  return {
    currentInqUser: null,
    newUserEntry: null,
    currentInqUserRole: null,
    newUserRoleEntry: null,
    authenticating: false,
    loggingOut: false,
    authError,
    authType,
    user: null,
    error: null,
    permissions: {
      'sale-order': []
    },
    uiPermissions: []
  }
}

// initial state
const state = getDefaultState()

const getters = {
  user: state => state.user,
  isAdmin: state => state.user.role_code === 'admin',
  loggedIn: state => {
    return state.accessToken ? true : false;
  },

  inquiryUser : state => {
    return state.inquiryUser;
  },

  isUserInquiry: state => {
    return state.currentInqUser === null ? false : true;
  },

  isNewUser: state => {
      return state.newUserEntry === null ? false : true;
  },

  newUserEntry: state => {
    return state.newUserEntry;
  },

  inquiryUserRole : state => {
    return state.inquiryUserRole;
  },

  userRoleInquiry: state => {
    return state.currentInqUserRole === null ? false : true;
  },

  newUserRole: state => {
      return state.newUserRoleEntry === null ? false : true;
  },

  newUserRoleEntry: state => {
    return state.newUserRoleEntry;
  },

  hasPermission: state => (file, action) => {
    return state.permissions[file].find(element => element === action)
  }, 
  hasUIPermission: state => view_code => {
    let hasAccess 
    if (state.user && state.user.role_code === "admin") return true
    if (view_code === "" || !view_code) return true

    hasAccess = state.uiPermissions.filter(el => {
      return parseInt(el) === parseInt(view_code)
    })
    return hasAccess.length > 0

  },
  layout: state => {
    if ((state.authenticating && state.authType === AuthType.Verify) || state.loggingOut) return 'loading-layout'
    if (state.user) return 'principal-layout'
    if (!state.user) return 'login-layout'
  }
   
};

const actions = {
  resetState ({ commit, dispatch }) {
    commit('resetState')
    dispatch("cm/resetState", {}, { root: true });
    dispatch("inventory/resetState", {}, { root: true });    
    dispatch("purchase/resetState", {}, { root: true });
    dispatch("ivTransaction/resetState", {}, { root: true });
  },

  async login({ commit, dispatch }, { user_code, password }) {
    commit("loginRequest", AuthType.Login);

    try {
      const response = await UserService.login(user_code, password);
      TokenService.saveToken(response.data.accessToken);
      TokenService.saveRefreshToken(response.data.refreshToken)
      
      commit('loginSuccess', { user: response.data.user, authType: AuthType.Login })

      dispatch('getUIPermissions')
      router.push('/dashboard')
    } catch (error) {
      commit("loginError", { error: error.message, authType: AuthType.Login });
    }
  },
  async logout({ commit, dispatch }, authType = AuthType.Logout) {
    try {
      commit("loginRequest", authType);
      router.push('/')
      await UserService.logout();
    } catch (e) { 
      throw e     
    } finally {
      commit("logoutSuccess")
      TokenService.clear();
      dispatch('resetState')
      router.replace('/login')
    }
  },
  async verify({ commit, dispatch, state }) {
    if (!state.user) {
      router.push('/login')
      return
    }

    try {
      commit("loginRequest", AuthType.Verify);
      const response = await UserService.verify()

      commit('loginSuccess', { user: response.data.user, authType: AuthType.Verify })

      dispatch('getUIPermissions')
    } catch (error) {
      commit("loginError", { error: "Sesión desconectada", authType: AuthType.Verify });
      throw error
    } 
  },
  async refreshToken({ commit, dispatch }) {
    try {
      commit("loginRequest", AuthType.RefshToken);
      TokenService.saveToken(TokenService.getRefreshToken())

      const response = await UserService.refreshToken()
      TokenService.saveToken(response.data.accessToken)
      TokenService.saveRefreshToken(response.data.refreshToken)

      commit('loginSuccess', { user: response.data.tokenDetails, authType: AuthType.RefshToken })
      return response
    } catch (error) {
      commit("loginError", { error: "Sesión desconectada", authType: AuthType.RefshToken });
      dispatch('logout', AuthType.RefshToken)
      throw error
    }
  },
  setInquiryUserRole ({ commit },role_code ){
    commit("setUserRoleInquiry",role_code)
  },
  setNewUserRole ({ commit }, role_code ){
    commit("setNewUserRole",role_code)
  },
  setInquiryUser ({ commit },userCode ){
    commit("setUserInquiry",userCode)
  },
  setNewUserCode ({ commit }, userCode ){
    commit("newUserCode",userCode)
  },

  async getUIPermissions({ commit }) {
    try {
      const response = await UserService.getUserRoleUIPermissions()
      commit('SET_USER_ROLE_UI_PERMISSION', response.data)
    } catch (error) {
      throw error
    }
  },
};

const mutations = {
  resetState (state) {
    Object.assign(state, getDefaultState(state.authError, state.authType))
  },

  loginRequest(state, payload) {
    state.authenticating = true;
    state.authType = payload;

    if (payload === AuthType.Logout){
      state.loggingOut = true
    }
  },

  loginSuccess(state, payload) {
    state.authenticating = false
    state.user = payload.user;
    state.authType = payload.authType;
    state.authError = "";
  },

  loginError(state, payload) {
    state.authenticating = false;
    state.authError = payload.error;
    state.authType = payload.authType;
    // Si falla el refresh token
    if (payload.authType === AuthType.RefshToken){
      state.loggingOut = true
    }
  },

  logoutSuccess(state) {
    state.accessToken = "";
    state.user = null;
    state.authenticating = false;
  },
  setUserRoleInquiry(state, role_code){
    state.currentInqUserRole = role_code;
  },

  setNewUserRole(state, role_code){
    state.newUserRoleEntry = role_code;
  },

  setUserInquiry(state, userCode){
    state.currentInqUser = userCode;
  },
  newUserCode(state, userCode){
    state.newUserEntry = userCode;
  },
  SET_PERMISSION(state, payload) {
    state.permissions[payload.file].push('u')
  },
  SET_USER_ROLE_UI_PERMISSION(state, payload) {
    state.uiPermissions = payload
    state.authenticating = false
  }
};

export const auth = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/auth.module.js